import { BaseElement } from '../../../base-element';
import type { TermsDto } from '../../../../api/types';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface TermsSectionChangeDetail {
  accepted: boolean;
}

export class TermsSection extends BaseElement {
  private _terms: TermsDto | null = null;
  private _accepted = false;
  private _error = '';

  get terms(): TermsDto | null {
    return this._terms;
  }

  set terms(value: TermsDto | null) {
    this._terms = value;
    this.updateRender();
  }

  get accepted(): boolean {
    return this._accepted;
  }

  set accepted(value: boolean) {
    this._accepted = value;
    this.updateRender();
  }

  get error(): string {
    return this._error;
  }

  set error(value: string) {
    this._error = value;
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private handleCheckboxChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.emit<TermsSectionChangeDetail>('terms-change', { accepted: target.checked });
  };

  private updateRender(): void {
    const terms = this._terms;
    const pdfSource = terms?.html ?? '';

    this.render(`
      <section class="flex flex-col gap-3" data-testid="terms-section">
        <div class="text-sm font-semibold uppercase tracking-widest text-neutral-600">Terms and Conditions</div>
        <div class="rounded-xl border border-neutral-200 bg-white p-4">
          <div class="h-[300px] overflow-hidden rounded-lg border border-neutral-200 md:h-[400px]">
            ${pdfSource
              ? `
                <embed
                  src="${escapeHtml(pdfSource)}"
                  type="application/pdf"
                  class="h-full w-full"
                />
              `
              : `
                <div class="flex h-full items-center justify-center text-sm text-neutral-500">
                  Terms are unavailable.
                </div>
              `}
          </div>
          <div class="mt-4 flex flex-col gap-2">
            <label class="flex items-center gap-2 text-sm text-neutral-700">
              <input type="checkbox" ${this._accepted ? 'checked' : ''} />
              <span>I have read and agree to the Terms &amp; Conditions above</span>
            </label>
            ${terms?.version ? `
              <div class="text-xs uppercase tracking-widest text-neutral-500">Version: ${escapeHtml(terms.version)}</div>
            ` : ''}
            ${this._error ? `
              <p class="text-xs text-error-600">${escapeHtml(this._error)}</p>
            ` : ''}
          </div>
        </div>
      </section>
    `);

    const checkbox = this.$('input[type="checkbox"]');
    if (checkbox) {
      checkbox.addEventListener('change', this.handleCheckboxChange);
    }
  }
}

customElements.define('dt-terms-section', TermsSection);
